import { AfterViewInit, Component, ElementRef, HostListener, Input, OnInit, Renderer2, ViewChild } from '@angular/core';
import { CommonService } from '../../services/common/common.service';
import { chunk } from 'lodash-es';
import { MetadataService } from '../../services/metadata/metadata.service';

export interface ContentChangeEvent {
  source: string;
  text: string;
  delta: { ops: { insert?: string; delete?: number; retain?: number }[] };
}

// INFO: GenericInputComponent replaces the old GenericQuillComponent with a simpler, flexible contenteditable div which supports colorization.
@Component({
  standalone: false,
  selector: 'res-generic-input',
  templateUrl: './generic-input.component.html',
  styleUrls: ['./generic-input.component.scss']
})
export class GenericInputComponent implements OnInit, AfterViewInit {
  @ViewChild('editor', { static: true }) editorRef: ElementRef<HTMLDivElement>;

  @HostListener('document:click', ['$event']) onClick(event: MouseEvent) {
    const target = event?.target as HTMLElement;
    if (!this.editorRef?.nativeElement.contains(target) && !target?.closest('.autocomplete-list')) {
      this.closeMetadataSuggestionsList();
    }
  }

  @Input() queryString: string;
  @Input() colorizeOnQueryChange = false;
  @Input() keyPressEvent: (event?: any) => void;
  @Input() workflowMode = false;
  @Input() automationId = '';
  @Input() inputChangedEvent: (event?: ContentChangeEvent) => void;
  @Input() isAdvanceSearchFromWorkFlow = false;
  @Input() readonly = false;
  @Input() showMetadataSuggestions = false;

  metadataList = [];
  filteredMetadataSuggestions = [];
  activeIndex: number = -1; // -1 means nothing is active
  posSuggestionList = { top: 0, left: 0 };
  suggestionListEnterClick = false;
  lastWordFromInputText: string;
  suggestionListFirstOpen = true;
  readonly suggestionListMaxWidth = 250;

  previousText = '';

  constructor(private commonService: CommonService, private renderer: Renderer2, private metadataService: MetadataService) { }

  getText(): string {
    return this.editorRef?.nativeElement?.innerText ?? '';
  }

  setText(text: string): void {
    const oldText = this.previousText;
    this.applyText(text);
    if (this.inputChangedEvent) {
      this.inputChangedEvent({
        source: 'api',
        text,
        delta: { ops: [{ insert: text }] }
      });
    }
  }

  getSelection(): { index: number; length: number } | null {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return null;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    const preRange = document.createRange();
    preRange.selectNodeContents(editor);
    preRange.setEnd(range.startContainer, range.startOffset);
    return { index: preRange.toString().length, length: range.toString().length };
  }

  setSelection(range: { index: number; length: number }): void {
    this.setSelectionAtOffset(range.index);
  }

  enable(): void {
    this.editorRef?.nativeElement?.setAttribute('contenteditable', 'true');
  }

  disable(): void {
    this.editorRef?.nativeElement?.setAttribute('contenteditable', 'false');
  }

  hasScrollOverflow(): boolean {
    const el = this.editorRef?.nativeElement;
    return el ? el.scrollHeight > el.clientHeight : false;
  }

  getEditorElement(): HTMLDivElement | null {
    return this.editorRef?.nativeElement ?? null;
  }

  ngOnInit(): void {
    if (this.showMetadataSuggestions) {
      this.metadataService.tokenizationTypes().then((responseProperties) => {
        if (responseProperties) {
          const allMetadata = [];
          responseProperties.forEach((element) => {
            allMetadata.push({
              name: element.name + '::',
              description: element.description,
            });
          });
          this.metadataList = allMetadata.sort();
        }
      });
    }
  }

  ngAfterViewInit(): void {
    const editor = this.editorRef.nativeElement;
    const queryStr = this.queryString?.length > 0 ? this.queryString : '';
    this.previousText = queryStr;

    this.renderer.setAttribute(editor, 'query', queryStr);
    this.renderer.setAttribute(editor, 'data-automation-id', this.automationId + '-query-input');
    this.renderer.setAttribute(editor, 'spellcheck', 'false');

    if (this.readonly) {
      editor.setAttribute('contenteditable', 'false');
      this.applyReadonlyStyles(editor);
    } else {
      editor.setAttribute('contenteditable', 'true');
    }

    this.highlightNOW(queryStr);
  }

  highlightNOW(queryStr?: string, isQueryStringEmpty?: boolean, isInitialQuery?: boolean, isWorkflow?: boolean): void {
    if ((window as any).hljs == null && !this.commonService.currentBrowserIsIE) return;
    const editor = this.editorRef?.nativeElement;
    if (!editor) return;

    if (queryStr || isQueryStringEmpty) {
      editor.innerText = queryStr ?? '';
      this.previousText = queryStr ?? '';
      this.renderer.setAttribute(editor, 'query', (queryStr ?? '').trim().replace(/\u00a0/g, ' ').replace(/(&nbsp;)/gi, ''));
    }

    const textToHighlight = editor.innerText;
    const savedSel = this.getSelection();
    this.applyHighlight(textToHighlight);
    // Only restore the caret when this editor is actually focused.
    if (savedSel && document.activeElement === editor) {
      this.setSelectionAtOffset(savedSel.index);
    }
  }

  colorize(): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return;
    const text = editor.innerText;
    const savedSel = this.getSelection();
    this.applyHighlight(text.length > 0 ? text : '');
    // Only restore the caret when this editor is actually focused.
    // Calling sel.addRange() while another element has focus would steal
    // focus away from the element the user is currently interacting with.
    if (savedSel && document.activeElement === editor) {
      this.setSelectionAtOffset(savedSel.index);
    }
  }

  capitalizeKeywords(str: string): string {
    let caps = str.replace(/\b(and)(?=\s)(?=(?:[^"']|["'][^"']*['"])*$)/gi, 'AND');
    caps = caps.replace(/\b(or)(?=\s)(?=(?:[^"']|["'][^"']*['"])*$)/gi, 'OR');
    caps = caps.replace(/\b(not)(?=\s)(?=(?:[^"']|["'][^"']*['"])*$)/gi, 'NOT');
    caps = caps.replace(/\n/g, '');
    const decapitalizeKeywordsCheck = str.indexOf('AND') === -1 && str.indexOf('OR') === -1 && str.indexOf('NOT');
    if (caps?.length > 1 && caps.indexOf('"') !== -1 && decapitalizeKeywordsCheck) {
      caps = this.deCapitalizeKeywordsWithinQuotes(caps);
    }
    return caps;
  }

  deCapitalizeKeywordsWithinQuotes(capitalizedStr: string): string {
    if (capitalizedStr?.length > 1 && capitalizedStr.indexOf('"') !== -1 &&
      (capitalizedStr.indexOf('AND') !== -1 || capitalizedStr.indexOf('OR') !== -1 || capitalizedStr.indexOf('NOT') !== -1)) {
      let count = 0;
      let position = capitalizedStr.indexOf('"');
      const positionIndices = [position];

      let indicesToUseTheOriginalString = [];

      while (position !== -1) {
        count++;
        position = capitalizedStr.indexOf('"', position + 1);
        if (position !== -1) {
          positionIndices.push(position);
        }
      }

      const quotesAreInPairs = positionIndices.length % 2 === 0 ? true : false;
      if (!quotesAreInPairs) {
        positionIndices.push(capitalizedStr.length);
      }

      indicesToUseTheOriginalString = chunk(positionIndices, 2);

      indicesToUseTheOriginalString.forEach((strToReplaceIndices) => {
        let queryInQuotes = capitalizedStr.substring(strToReplaceIndices[0], strToReplaceIndices[1]);
        const regForAND = /AND/gi;
        const regForOR = /OR/gi;
        const regForNOT = /NOT/gi;
        queryInQuotes = queryInQuotes.replace(regForAND, 'and').replace(regForOR, 'or').replace(regForNOT, 'not');
        capitalizedStr = capitalizedStr.substring(0, strToReplaceIndices[0]) + queryInQuotes + capitalizedStr.substring(strToReplaceIndices[0] + queryInQuotes.length, capitalizedStr.length);
      });
    }
    return capitalizedStr;
  }

  onContentChanged(event: Event): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return;

    const rawText = editor.innerText;
    const capitalizedText = this.capitalizeKeywords(rawText);
    const trimmedText = capitalizedText.trim().replace(/\u00a0/g, ' ').replace(/(&nbsp;)/gi, '');

    // Keep 'query' attribute in sync (mirrors the original Quill behaviour)
    this.renderer.setAttribute(editor, 'query', trimmedText);

    // Always apply syntax highlighting in real-time as the user types.
    // The cursor position is saved before innerHTML is replaced and restored
    // immediately after, so the caret never jumps.
    const savedSel = this.getSelection();
    this.applyHighlight(capitalizedText);
    if (savedSel) this.setSelectionAtOffset(savedSel.index);

    // Build a ContentChangeEvent that mirrors the ngx-quill ContentChange shape
    const changeEvent = this.buildChangeEvent(event as InputEvent, capitalizedText);
    this.previousText = capitalizedText;

    if (this.inputChangedEvent) {
      this.inputChangedEvent(changeEvent);
    }

    if (this.showMetadataSuggestions && capitalizedText?.length > 1) {
      const inputType = (event as InputEvent).inputType;
      const insertedChar = (event as InputEvent).data ?? '';
      if (inputType === 'insertText' && (insertedChar.trim() === '' || insertedChar === '(')) {
        this.closeMetadataSuggestionsList();
      } else {
        this.updateMetadataSuggestions(capitalizedText);
      }
    } else {
      this.closeMetadataSuggestionsList();
    }
  }

  onPaste(event: ClipboardEvent): void {
    // Strip rich formatting from pasted content — same intent as Quill's clipboard matcher.
    event.preventDefault();
    const plain = (event.clipboardData?.getData('text/plain') ?? '')
      .replace(/\u00a0/g, ' ')
      .replace(/\n/g, ' ');

    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(plain);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    }

    // Manually fire an input event so onContentChanged picks up the pasted text.
    this.editorRef.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
  }

  blur(event: Event): void {
    if (!this.colorizeOnQueryChange) {
      const editor = this.editorRef?.nativeElement;
      if (editor) {
        this.applyHighlight(editor.innerText);
      }
    }
  }

  keyPress(event: KeyboardEvent): void {
    this.handleMetadataSuggestionListKeyPress(event);

    if (this.keyPressEvent) {
      this.keyPressEvent(event);
    }

    if (event.ctrlKey) {
      // do not execute keypress event on clipboard events
      switch (event.key.toLowerCase()) {
        case 'x':
        case 'v':
        case 'c':
          return;
      }
    }

    const sel = window.getSelection();
    if (sel && !sel.isCollapsed && event.key.length === 1) {
      const savedSel = this.getSelection();
      if (savedSel && savedSel.length > 0) {
        const editor = this.editorRef?.nativeElement;
        if (editor) {
          const text = editor.innerText;
          const newText = text.substring(0, savedSel.index) + event.key + text.substring(savedSel.index + savedSel.length);
          editor.innerText = this.capitalizeKeywords(newText);
          this.editorRef.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));
          this.setSelectionAtOffset(savedSel.index + 1);
          event.stopPropagation();
          event.preventDefault();
        }
      }
    }
  }

  closeMetadataSuggestionsList(): void {
    this.filteredMetadataSuggestions = [];
    this.activeIndex = -1;
  }

  setHighlightOnWord(word: string): string {
    if (!this.lastWordFromInputText || !word) return word;
    const regex = new RegExp(this.lastWordFromInputText, 'gi');
    return word.replace(regex, match => `<span class="input-metadata-matched">${match}</span>`);
  }

  extractLastWord(text: string): string {
    const words = text.split(/\s+/);
    return words?.length > 0 ? words[words.length - 1] : null;
  }

  hasToBeDisplayedOnLeft(caretPosition: DOMRect): boolean {
    const editorRect = this.editorRef?.nativeElement?.getBoundingClientRect();
    if (editorRect && caretPosition) {
      const remainingSpaceOnRight = editorRect.right - caretPosition.right;
      if (remainingSpaceOnRight < (this.suggestionListMaxWidth - 40)) {
        return true;
      }
    }
    return false;
  }

  updateSuggestionListPosition(): void {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0).cloneRange();
    const rects = range.getClientRects();
    if (!rects.length) return;

    const caretRect = rects[0];
    const flipLeft = this.hasToBeDisplayedOnLeft(caretRect);
    if (caretRect && window.top) {
      const leftPos = flipLeft ? caretRect.left + window.scrollX - this.suggestionListMaxWidth : caretRect.left + window.scrollX;
      this.posSuggestionList = {
        top: caretRect.top + window.scrollY + 20,
        left: leftPos,
      };
    }
  }

  selectMetadata(item: string): void {
    const editor = this.editorRef?.nativeElement;
    if (editor) {
      const text = editor.innerText;
      const words = text.split(/\s+/);
      if (words?.length > 0) {
        words[words.length - 1] = item;
        editor.innerText = words.join(' ');
        this.editorRef.nativeElement.dispatchEvent(new Event('input', { bubbles: true }));

        setTimeout(() => {
          this.closeMetadataSuggestionsList();
          this.placeCaretAtEnd(editor);
        });
      }
    }
  }

  placeCaretAtEnd(el: HTMLElement): void {
    if (el) {
      const emptyRange = document.createRange();
      emptyRange.selectNodeContents(el);
      emptyRange.collapse(false);
      const currentCaretPosition = window.getSelection();
      if (currentCaretPosition) {
        currentCaretPosition.removeAllRanges();
        currentCaretPosition.addRange(emptyRange);
      }
    }
  }

  activateIndexOnMouseOver(event: MouseEvent, index: number): void {
    this.activeIndex = index;
  }

  handleMetadataSuggestionListKeyPress(event: KeyboardEvent): void {
    switch (event?.key) {
      case 'ArrowDown': {
        this.activeIndex = (this.activeIndex + 1) % this.filteredMetadataSuggestions.length;
        event.preventDefault();
        this.scrollToSelectedItem();
        break;
      }
      case 'ArrowUp': {
        this.activeIndex =
          (this.activeIndex - 1 + this.filteredMetadataSuggestions.length) %
          this.filteredMetadataSuggestions.length;
        event.preventDefault();
        this.scrollToSelectedItem();
        break;
      }
      case 'Enter': {
        if (this.filteredMetadataSuggestions?.length > 0) {
          if (this.activeIndex !== -1) {
            this.selectMetadata(this.filteredMetadataSuggestions[this.activeIndex]?.name);
            event.preventDefault();
            this.suggestionListEnterClick = true; //prevents triggering search from Enter in search bar, when suggestion list is open
          } else {
            this.closeMetadataSuggestionsList();
          }
        }
        break;
      }
      case 'Escape': {
        this.closeMetadataSuggestionsList();
        break;
      }
    }
    if (event?.code === 'Space') {
      this.closeMetadataSuggestionsList();
    }
  }

  trackByFn(index: number): number {
    return index;
  }

  scrollToSelectedItem(): void {
    setTimeout(() => {
      const suggestionListElement = document.getElementById('suggestionList');
      if (suggestionListElement) {
        const ulRect = suggestionListElement.getBoundingClientRect();
        const selectedListItem = Array.from(suggestionListElement.children)?.find(
          (li) => li.className?.includes('active-metadata')
        );
        if (selectedListItem && ulRect) {
          const listItemRectangle = selectedListItem.getBoundingClientRect();
          const listItemIsVisible =
            listItemRectangle?.bottom >= ulRect.top && listItemRectangle?.top <= ulRect.bottom;
          if (!listItemIsVisible) {
            selectedListItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }
    });
  }

  applyHighlight(text: string): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return;
    if ((window as any).hljs == null || this.commonService.currentBrowserIsIE) {
      editor.innerText = text;
      return;
    }

    const cleanText = text.replace(/\n+$/, '');
    const highlighted = (window as any).hljs.highlightAuto(cleanText);
    editor.innerHTML = highlighted.value.replace(/\u00a0/g, ' ');
  }

  applyText(text: string): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return;
    this.previousText = text;
    this.applyHighlight(text);
    // Keep 'query' attribute in sync for programmatic updates (setText, clearQuery, loadSearch, etc.)
    this.renderer.setAttribute(editor, 'query', text.trim().replace(/\u00a0/g, ' ').replace(/(&nbsp;)/gi, ''));
  }

  setSelectionAtOffset(offset: number): void {
    const editor = this.editorRef?.nativeElement;
    if (!editor) return;
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    let remaining = offset;
    let node: Text | null;
    while ((node = walker.nextNode() as Text | null)) {
      if (remaining <= node.length) {
        const range = document.createRange();
        range.setStart(node, remaining);
        range.collapse(true);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        return;
      }
      remaining -= node.length;
    }
    this.placeCaretAtEnd(editor);
  }

  /**
   * Builds a ContentChangeEvent whose `delta` shape mirrors the parts of ngx-quill's
   * ContentChange that parent components actually inspect.
   *
   * Input delta ops inspected by callers:
   *   ops[0|1].delete  → was content deleted?
   *   ops[0|1].insert  → what character/string was inserted?
   */
  buildChangeEvent(inputEvent: InputEvent, currentText: string): ContentChangeEvent {
    let delta: ContentChangeEvent['delta'];
    const inputType = inputEvent?.inputType ?? '';
    const insertedChar = inputEvent?.data ?? '';

    if (inputType.startsWith('delete')) {
      // Something was deleted: ops[1] carries the delete count
      delta = {
        ops: [
          { retain: currentText.length },
          { delete: Math.max(1, this.previousText.length - currentText.length) },
        ]
      };
    } else {
      // Insert (typing, paste, etc.): ops[1] carries the inserted string
      delta = {
        ops: [
          { retain: this.previousText.length },
          { insert: inputType === 'insertText' ? insertedChar : currentText.slice(this.previousText.length) },
        ]
      };
    }

    return {
      source: 'user',
      text: currentText,
      delta
    };
  }

  updateMetadataSuggestions(text: string): void {
    this.lastWordFromInputText = this.extractLastWord(text.trim());
    if (this.lastWordFromInputText?.startsWith('(')) {
      this.lastWordFromInputText = this.lastWordFromInputText.substring(1);
    }
    if (this.lastWordFromInputText?.length > 1) {
      this.filteredMetadataSuggestions = this.metadataList.filter((s) => s.name.includes(this.lastWordFromInputText));
      if (this.filteredMetadataSuggestions?.length > 0) {
        if (this.suggestionListFirstOpen) {
          this.updateSuggestionListPosition();
          this.suggestionListFirstOpen = false;
        } else {
          setTimeout(() => this.updateSuggestionListPosition());
        }
      } else {
        this.closeMetadataSuggestionsList();
      }
    } else {
      this.closeMetadataSuggestionsList();
    }
  }

  applyReadonlyStyles(editor: HTMLElement): void {
    editor.style.border = 'none';
    editor.style.overflowY = 'unset';
    editor.style.padding = '0px';
    editor.style.lineHeight = '18px';
    editor.style.fontFamily = 'Roboto, sans-serif';
    editor.style.fontSize = '13px';
    editor.style.fontWeight = '400';
  }
}
